import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  type ModalProps,
  spacing,
  ModalContainer as StyledModalContainer,
  Alert,
} from '@retailer-platform/shared-components'
import { CloseIcon } from '@instacart/ids-core'
import {
  Button,
  LegacyRadio,
  Text,
  TextArea,
  IconButton,
} from '@retailer-platform/shared-components/src/tds'
import React, { type FunctionComponent, useState, useMemo } from 'react'
import { DividerBase } from '@instacart/ids-customers'
import styled from '@emotion/styled'
import { useIsReadOnly } from '@retailer-platform/dashboard/access-control'
import { ReviewAction } from '../../api'
import { useDomainMessages } from '../../utils/domain/intl'
import { useIntegrationConfig } from '../integration-config-provider/IntegrationConfigProvider'
import { type UserPermittedActions } from '../../api/get-user-permitted-actions/api'
import {
  ExtraTextContentGrid,
  type ExtraTextContentGridProps,
} from '../extra-text-content-grid/ExtraTextContentGrid'

export type OnReviewAddSubmitProps = {
  action: ReviewAction
  comment: string
}

export type SubmitFeedbackModalProps = {
  onSubmit: (props: OnReviewAddSubmitProps) => void
  loading?: boolean
  defaultAction?: ReviewAction
  userPermittedActions?: UserPermittedActions
  extraContent?: React.ReactNode
  extraTextContent?: ExtraTextContentGridProps
} & ModalProps

export const SubmitFeedbackModal: FunctionComponent<
  React.PropsWithChildren<SubmitFeedbackModalProps>
> = (props: SubmitFeedbackModalProps) => {
  const i18n = useDomainMessages({
    title: 'approvalManagementLibrary.addReviewModal.title',
    comment: 'approvalManagementLibrary.actions.comment',
    cancel: 'approvalManagementLibrary.actions.cancel',
    approve: 'approvalManagementLibrary.actions.approve',
    publish: 'approvalManagementLibrary.actions.publish',
    reject: 'approvalManagementLibrary.actions.reject',
    submit: 'approvalManagementLibrary.actions.submit',
    commentDescription: 'approvalManagementLibrary.addReviewModal.commentDescription',
    approveDescription: 'approvalManagementLibrary.addReviewModal.approveDescription',
    publishDescription: 'approvalManagementLibrary.addReviewModal.publishDescription',
    approveBreakGlass: 'approvalManagementLibrary.addReviewModal.approveBreakGlass',
    approveBreakGlassDescription:
      'approvalManagementLibrary.addReviewModal.approveBreakGlassDescription',
    approveAndPublish: 'approvalManagementLibrary.addReviewModal.approveAndPublish',
    approveAndPublishDescription:
      'approvalManagementLibrary.addReviewModal.approveAndPublishDescription',
    requestChanges: 'approvalManagementLibrary.addReviewModal.requestChanges',
    requestChangesDescription: 'approvalManagementLibrary.addReviewModal.requestChangesDescription',
    rejectDescription: 'approvalManagementLibrary.addReviewModal.rejectDescription',
    leaveAComment: 'approvalManagementLibrary.actions.leaveAComment',
    optional: 'approvalManagementLibrary.general.optional',
    noActionsPermitted: 'approvalManagementLibrary.addReviewModal.noActionsPermitted',
    readOnlyWarning: 'approvalManagementLibrary.general.readOnlyWarning',
  })

  const config = useIntegrationConfig()
  const isReadOnly = useIsReadOnly()

  const { userPermittedActions } = props
  const { canApprove, canComment, canPublish, canBreakGlassPublish, canRevise, canCancel } =
    userPermittedActions || {
      canApprove: true,
      canComment: true,
      canPublish: true,
      canBreakGlassPublish: true,
      canRevise: true,
      canCancel: true,
    }

  const [action, setAction] = useState<ReviewAction | undefined>(props.defaultAction || undefined)
  const [comment, setComment] = useState<string>('')

  const commentBox = (
    <CommentBoxContainer id="commentBoxContainer">
      <Row>
        <Text typography="bodyEmphasized">{i18n.leaveAComment}</Text>
        {action !== ReviewAction.Comment && <Text typography="bodyRegular">{i18n.optional}</Text>}
      </Row>
      <TextArea
        data-testid="comment"
        placeholder={i18n.comment}
        value={comment}
        css={{ minHeight: 80 }}
        autoFocus
        onChange={(e: { target: { value: React.SetStateAction<string> } }) =>
          setComment(e.target.value)
        }
      />
    </CommentBoxContainer>
  )

  const commentRadioButton = canComment && (
    <RadioButton
      data-testid="commentRadioButton"
      action={ReviewAction.Comment}
      label={i18n.comment}
      isChecked={action === ReviewAction.Comment}
      description={i18n.commentDescription}
      setAction={setAction}
      commentBox={commentBox}
    />
  )

  const approveRadioButtonProps = useMemo(() => {
    if (config?.autoPublishOnApprove) {
      return {
        'data-testid': 'approveAndPublishRadioButton',
        action: ReviewAction.ApprovalAndPublish,
        label: i18n.approveAndPublish,
        isChecked: action === ReviewAction.ApprovalAndPublish,
        description: i18n.approveAndPublishDescription,
      }
    }

    return {
      'data-testid': 'approveRadioButton',
      action: ReviewAction.Approval,
      label: i18n.approve,
      isChecked: action === ReviewAction.Approval,
      description: i18n.approveDescription,
    }
  }, [action, config?.autoPublishOnApprove, i18n])

  const approveRadioButton = canApprove && (
    <RadioButton {...approveRadioButtonProps} setAction={setAction} commentBox={commentBox} />
  )

  const publishRadioButton = canPublish && (
    <RadioButton
      data-testid="publishRadioButton"
      action={ReviewAction.Publish}
      label={i18n.publish}
      isChecked={action === ReviewAction.Publish}
      description={i18n.publishDescription}
      setAction={setAction}
      commentBox={commentBox}
    />
  )

  // Only display the break-glass option if there is no publish option
  const breakGlassPublishRadioButton = !canPublish && canBreakGlassPublish && (
    <RadioButton
      data-testid="approveBreakGlassRadioButton"
      action={ReviewAction.BreakGlassPublish}
      label={i18n.approveBreakGlass}
      isChecked={action === ReviewAction.BreakGlassPublish}
      description={i18n.approveBreakGlassDescription}
      setAction={setAction}
      commentBox={commentBox}
    />
  )
  const requestChangesRadioButton = canRevise && (
    <RadioButton
      data-testid="requestChangesRadioButton"
      action={ReviewAction.RequestChanges}
      label={i18n.requestChanges}
      isChecked={action === ReviewAction.RequestChanges}
      description={i18n.requestChangesDescription}
      setAction={setAction}
      commentBox={commentBox}
    />
  )
  const rejectRadioButton = canCancel && (
    <RadioButton
      data-testid="rejectRadioButton"
      action={ReviewAction.Cancel}
      label={i18n.reject}
      isChecked={action === ReviewAction.Cancel}
      description={i18n.rejectDescription}
      setAction={setAction}
      commentBox={commentBox}
    />
  )

  const noActionsPermitted =
    !commentRadioButton &&
    !approveRadioButton &&
    !publishRadioButton &&
    !breakGlassPublishRadioButton &&
    !requestChangesRadioButton &&
    !rejectRadioButton
  const noActionsFallbackText = noActionsPermitted && (
    <Text typography="bodyRegular">{i18n.noActionsPermitted}</Text>
  )

  if (isReadOnly) {
    return (
      <ModalContainer {...props}>
        <StyledModalBody>
          <Alert variant="warning">{i18n.readOnlyWarning}</Alert>
        </StyledModalBody>
        <ModalFooter>
          <Button
            variant="secondary"
            name="cancel"
            data-testid="cancel"
            onClick={props.onRequestClose}
          >
            {i18n.cancel}
          </Button>
        </ModalFooter>
      </ModalContainer>
    )
  }

  const extraContent =
    props.extraContent ||
    (props.extraTextContent && <ExtraTextContentGrid {...props.extraTextContent} />)

  return (
    <ModalContainer {...props}>
      {extraContent && (
        <>
          <StyledModalBody>{extraContent}</StyledModalBody>
          <DividerBase />
        </>
      )}

      <StyledModalBody>
        {commentRadioButton}
        {approveRadioButton}
        {publishRadioButton}
        {breakGlassPublishRadioButton}
        {requestChangesRadioButton}
        {rejectRadioButton}
        {noActionsFallbackText}
      </StyledModalBody>

      <ModalFooter>
        <Button.Group>
          <Button
            variant="secondary"
            name="cancel"
            data-testid="cancel"
            onClick={props.onRequestClose}
          >
            {i18n.cancel}
          </Button>
          <Button
            variant="primary"
            name="submit"
            data-testid="submit"
            loading={props.loading}
            onClick={() => props.onSubmit({ action: action!, comment: comment })}
            disabled={noActionsPermitted || !action}
          >
            {i18n.submit}
          </Button>
        </Button.Group>
      </ModalFooter>
    </ModalContainer>
  )
}
const CloseButtonWrapper = styled.div(() => ({
  margin: `${spacing.X16} 0 0 ${spacing.X16}`,
  position: 'absolute',
}))

const ModalContainer: FunctionComponent<React.PropsWithChildren<SubmitFeedbackModalProps>> = (
  props: SubmitFeedbackModalProps
) => {
  const i18n = useDomainMessages({
    title: 'approvalManagementLibrary.addReviewModal.title',
  })

  return (
    <Modal hideCloseButton {...props} maxHeight="90%" style={{ overlay: { display: 'flex' } }}>
      <StyledModalContainer>
        <CloseButtonWrapper>
          <IconButton onClick={props.onRequestClose} variant="tertiary" icon={CloseIcon} />
        </CloseButtonWrapper>
        <ModalHeader>{i18n.title}</ModalHeader>
        {props.children}
      </StyledModalContainer>
    </Modal>
  )
}

const RadioButton = ({
  action,
  description,
  isChecked,
  label,
  setAction,
  commentBox,
  ...props
}: {
  action: ReviewAction
  label: string
  isChecked: boolean
  description: string
  setAction: (action: ReviewAction) => void
  commentBox?: JSX.Element
}) => (
  <>
    <div>
      <LegacyRadio
        id={action}
        value={action}
        label={label}
        checked={isChecked}
        onChange={() => setAction(action)}
        {...props}
      />
      <Text css={{ marginLeft: 30 }} typography="accentRegular">
        {description}
      </Text>
    </div>
    {isChecked && commentBox}
  </>
)

const StyledModalBody = styled(ModalBody)({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.X16,
  overflowY: 'auto',
  flexShrink: 1,
})

const CommentBoxContainer = styled.div({
  marginLeft: 30,
})

const Row = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: spacing.X4,
})
